import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import api from '../api';
import PageHeader from '../components/PageHeader';
import BotSelector from '../components/BotSelector';
import { Avatar, Ic, Spinner } from '../components/ui';

const STATUS = {
  open:        { label: 'Açık',    cls: 'warning' },
  in_progress: { label: 'İşlemde', cls: 'info' },
  closed:      { label: 'Kapalı',  cls: 'neutral' },
};
const PRIORITY = {
  low:    { label: 'Düşük',  color: '#7f91a4' },
  normal: { label: 'Normal', color: '#6ab3f3' },
  high:   { label: 'Yüksek', color: '#e5a443' },
  urgent: { label: 'Acil',   color: '#ef5b5b' },
};

const CSS = `
.tk-inbox { display:grid; grid-template-columns:340px minmax(0,1fr); height:max(520px, calc(100vh - 330px)); padding:0; overflow:hidden; }
.tk-list { border-right:1px solid var(--border3); overflow-y:auto; min-width:0; }
.tk-detail { display:flex; flex-direction:column; min-width:0; background:#0e1621; }
.tk-back { display:none !important; }
@media (max-width: 900px) {
  .tk-inbox { grid-template-columns:1fr; height:auto; }
  .tk-list { border-right:none; max-height:none; }
  .tk-detail { position:fixed; left:0; right:0; bottom:0; top:var(--topbar-h); z-index:150; }
  .tk-back { display:inline-flex !important; }
  .tk-empty { display:none !important; }
}
`;

export default function Tickets() {
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ status: '', priority: '' });
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const msgsRef = useRef(null);

  useEffect(() => {
    api.get('/bots').then(r => { setBots(r.data || []); if (r.data?.length) setSelectedBot(r.data[0]); }).catch(() => {});
    api.get('/users/admins').then(r => setAdmins(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { if (selectedBot) { loadTickets(); loadStats(); } }, [selectedBot, filter]);
  useLayoutEffect(() => { const el = msgsRef.current; if (el) el.scrollTop = el.scrollHeight; }, [selected?.messages]);

  async function loadTickets() {
    setLoading(true);
    try {
      const params = { bot_id: selectedBot.id };
      if (filter.status) params.status = filter.status;
      if (filter.priority) params.priority = filter.priority;
      const res = await api.get('/tickets', { params }); setTickets(res.data || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  }
  async function loadStats() { try { const res = await api.get('/tickets/stats/overview', { params: { bot_id: selectedBot.id } }); setStats(res.data); } catch (e) { /* noop */ } }
  async function selectTicket(t) { try { const res = await api.get(`/tickets/${t.id}`); setSelected(res.data); } catch (e) { /* noop */ } }
  async function updateTicket(id, updates) {
    try {
      await api.patch(`/tickets/${id}`, updates); loadTickets(); loadStats();
      if (selected?.id === id) { const res = await api.get(`/tickets/${id}`); setSelected(res.data); }
    } catch (e) { /* noop */ }
  }
  async function sendReply(e) {
    e?.preventDefault();
    if (!reply.trim() || !selected) return;
    setSending(true);
    try { await api.post(`/tickets/${selected.id}/reply`, { message: reply }); setReply(''); const res = await api.get(`/tickets/${selected.id}`); setSelected(res.data); loadTickets(); }
    catch (err) { alert(err.response?.data?.error || 'Hata'); }
    setSending(false);
  }

  const counts = { '': stats?.total, open: stats?.open, in_progress: stats?.in_progress, closed: stats?.closed };

  return (
    <div className="page-enter">
      <style>{CSS}</style>
      <PageHeader title="Ticketlar" subtitle="Kullanıcı destek taleplerini yönet"
        botSelector={<BotSelector bots={bots.map(b => ({ ...b, pending_reports: b.open_tickets }))} selected={selectedBot} onSelect={b => { setSelectedBot(b); setSelected(null); }} />} />

      {stats && (
        <div className="stats-grid">
          {[
            { label: 'Toplam', value: stats.total || 0, color: 'var(--text)' },
            { label: 'Açık', value: stats.open || 0, color: 'var(--warning)' },
            { label: 'İşlemde', value: stats.in_progress || 0, color: 'var(--info)' },
            { label: 'Kapalı', value: stats.closed || 0, color: 'var(--success)' },
            { label: 'Yüksek öncelik', value: stats.high_priority || 0, color: 'var(--danger)' },
            { label: 'Atanmamış', value: stats.unassigned || 0, color: 'var(--purple)' },
          ].map(s => (
            <div key={s.label} className="stat-card"><div className="stat-value" style={{ color: s.color }}>{s.value}</div><div className="stat-label">{s.label}</div></div>
          ))}
        </div>
      )}

      <div className="flex-between" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className="seg">
          {[['', 'Tümü'], ['open', 'Açık'], ['in_progress', 'İşlemde'], ['closed', 'Kapalı']].map(([v, l]) => (
            <button key={v} className={filter.status === v ? 'on' : ''} onClick={() => setFilter(f => ({ ...f, status: v }))}>{l}{counts[v] != null ? ` · ${counts[v]}` : ''}</button>
          ))}
        </div>
        <select className="input" style={{ width: 'auto', minWidth: 160 }} value={filter.priority} onChange={e => setFilter(f => ({ ...f, priority: e.target.value }))}>
          <option value="">Tüm öncelikler</option>
          {Object.entries(PRIORITY).map(([v, p]) => <option key={v} value={v}>{p.label}</option>)}
        </select>
      </div>

      <div className="card tk-inbox">
        <div className="tk-list">
          {loading && !tickets.length && <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner /></div>}
          {!loading && !tickets.length && <div className="empty-state"><div className="icon"><Ic n="ticket" s={44} /></div><p>Ticket bulunamadı</p></div>}
          <div className="list">
            {tickets.map(t => {
              const s = STATUS[t.status] || STATUS.open;
              const p = PRIORITY[t.priority] || PRIORITY.normal;
              return (
                <div key={t.id} className={'list-row click' + (selected?.id === t.id ? ' on' : '')} onClick={() => selectTicket(t)} style={{ alignItems: 'flex-start' }}>
                  <Avatar id={t.telegram_id || t.id} name={t.full_name} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex-between" style={{ gap: 6 }}>
                      <span className="truncate" style={{ fontWeight: 600 }}>{t.full_name || 'Kullanıcı'}</span>
                      <span className="text-muted" style={{ fontSize: 12, flexShrink: 0 }}>#{t.id}</span>
                    </div>
                    <div className="truncate text-muted" style={{ fontSize: 13.5, margin: '1px 0 5px' }}>{t.last_message || '—'}</div>
                    <div className="flex-center gap-6" style={{ flexWrap: 'wrap' }}>
                      <span className={`badge badge-${s.cls}`}>{s.label}</span>
                      <span className="chip" style={{ background: p.color + '22', color: p.color }}>{p.label}</span>
                      <span className="text-muted" style={{ fontSize: 12 }}>{t.message_count || 0} mesaj · {new Date(t.updated_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                    {t.assigned_username && <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>Atanan: {t.assigned_username}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {selected ? (
          <div className="tk-detail">
            <div style={{ padding: '10px 12px', background: 'var(--surface)', borderBottom: '1px solid #0a1119' }}>
              <div className="flex-center gap-10">
                <button className="btn btn-icon tk-back" onClick={() => setSelected(null)}><Ic n="back" /></button>
                <Avatar id={selected.telegram_id || selected.id} name={selected.full_name} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="truncate" style={{ fontWeight: 600, fontSize: 15.5 }}>#{selected.id} · {selected.full_name}</div>
                  <div className="truncate text-muted" style={{ fontSize: 12.5 }}>ID: {selected.telegram_id} · {selected.bot_name}</div>
                </div>
                <button className="btn btn-icon" onClick={() => setSelected(null)} title="Kapat"><Ic n="close" /></button>
              </div>
              <div className="flex gap-6" style={{ flexWrap: 'wrap', marginTop: 10 }}>
                <select className="input" value={selected.status} style={{ width: 'auto', padding: '6px 30px 6px 10px', fontSize: 13 }} onChange={e => updateTicket(selected.id, { status: e.target.value })}>
                  {Object.entries(STATUS).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
                </select>
                <select className="input" value={selected.priority} style={{ width: 'auto', padding: '6px 30px 6px 10px', fontSize: 13 }} onChange={e => updateTicket(selected.id, { priority: e.target.value })}>
                  {Object.entries(PRIORITY).map(([v, p]) => <option key={v} value={v}>{p.label}</option>)}
                </select>
                <select className="input" value={selected.assigned_to || ''} style={{ width: 'auto', padding: '6px 30px 6px 10px', fontSize: 13 }} onChange={e => updateTicket(selected.id, { assigned_to: e.target.value || null })}>
                  <option value="">Atanmamış</option>{admins.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
                </select>
                {selected.status !== 'closed' && <button className="btn btn-sm btn-success" onClick={() => updateTicket(selected.id, { status: 'closed' })}><Ic n="check" s={15} />Kapat</button>}
              </div>
            </div>

            <div ref={msgsRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
              {(selected.messages || []).map(m => {
                const out = m.direction === 'out';
                const bg = out ? '#2b5278' : '#182533';
                const media = m.media_path ? '/uploads/' + m.media_path : null;
                return (
                  <div key={m.id} style={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                    <div style={{ maxWidth: 'min(460px, 85%)', background: bg, borderRadius: out ? '15px 15px 6px 15px' : '15px 15px 15px 6px', padding: '6px 10px 6px 11px', overflow: 'hidden' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: out ? '#b9dcff' : 'var(--link)', marginBottom: 2 }}>{out ? (m.sender_name || 'Admin') : (m.sender_name || selected.full_name)}</div>
                      {media && m.type === 'photo' && <img src={media} alt="" style={{ display: 'block', maxWidth: '100%', width: 260, borderRadius: 8, marginBottom: 4 }} />}
                      {media && m.type === 'file' && <a href={media} target="_blank" rel="noreferrer" className="flex-center gap-6" style={{ color: out ? '#fff' : 'var(--link)', marginBottom: 4 }}><Ic n="file" s={16} />{m.content || 'Dosya'}</a>}
                      {m.content && m.type !== 'file' && <span style={{ fontSize: 14.5, lineHeight: 1.42, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</span>}
                      <span style={{ float: 'right', margin: '6px 0 -3px 10px', fontSize: 12, color: out ? '#7da8d3' : '#6d7f8f' }}>{new Date(m.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {selected.status !== 'closed' ? (
              <form onSubmit={sendReply} style={{ display: 'flex', alignItems: 'flex-end', gap: 4, padding: '7px 8px', paddingBottom: 'max(7px, env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid #0a1119' }}>
                <textarea rows={1} value={reply} onChange={e => { setReply(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px'; }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Cevap yazın… (HTML destekli)"
                  style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 15, lineHeight: '22px', resize: 'none', height: 22, maxHeight: 140, margin: '9px 8px', fontFamily: 'inherit' }} />
                <button type="submit" className="btn btn-icon" disabled={!reply.trim() || sending} style={{ color: reply.trim() ? 'var(--link)' : 'var(--text3)' }}><Ic n="send" s={26} /></button>
              </form>
            ) : (
              <div style={{ padding: 14, textAlign: 'center', fontSize: 13.5, color: 'var(--text3)', background: 'var(--surface)', borderTop: '1px solid #0a1119' }}>Bu ticket kapatılmış</div>
            )}
          </div>
        ) : (
          <div className="tk-detail tk-empty" style={{ alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ background: 'rgba(30,44,58,.9)', color: '#e8edf2', fontSize: 14, padding: '6px 14px', borderRadius: 14 }}>Bir ticket seçin</span>
          </div>
        )}
      </div>
    </div>
  );
}
